import { useTranslation } from "react-i18next";
import { Outlet, useParams } from "react-router-dom";
import React, { Fragment, type ReactNode, useMemo, useState } from "react";
import { type TabOptions, TabsStore } from "src/lib/tabs-ui/TabsStore.tsx";

import { type RouterTabPath } from "src/lib/tabs/types.ts";

import { useDataRouterContext } from "src/lib/react-router/useDataRouterContext.ts";
import { theEnd } from "src/lib/tabs/theEnd.ts";
import { whenRoutePathIs } from "src/lib/tabs/whenRoutePathIs.ts";
import { SimpleTabsV2 } from "src/components/SimpleTabsV2/SimpleTabsV2.tsx";
import type { TFunction } from "i18next";
import { useRouterTabsV2 } from "src/lib/tabs/useRouterTabsV2.tsx";
import { LoadingOverlay } from "src/components/LoadingOverlay/LoadingOverlay.tsx";
import { replacePathParams } from "@exonn/utils";
import { QueryError } from "src/components/QueryError/QueryError.tsx";
import { css } from "@emotion/react";
import { useResourceStorageKeys } from "src/framework/useResourceStorageKeys.tsx";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { type ClientConfig } from "src/client-config/getClientConfig.ts";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { useResourceForm } from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import type {
  ValidFormData,
  ValidSaveFormResponseData,
} from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import { ResourceFormApiContext } from "src/components/Form/ResourceFormApiContext.tsx";
import {
  FormProvider as ReactHookFormFormProvider,
  useWatch,
} from "react-hook-form";
import { ResourceFormContext } from "src/components/Form/ResourceFormContext.tsx";
import { useModuleRoutePaths } from "src/system/useModuleRoutePaths.ts";
import { useModuleTabs } from "src/framework/layout/ModuleTabsProvider.tsx";
import { useContentLocale } from "src/auth/hooks/useContentLocale.tsx";
import { useOne } from "src/system/useOne.tsx";
import { useSubscribeTabsToFormUnsavedChanges } from "src/framework/form/useSubscribeTabsToFormUnsavedChanges.ts";
import type { FormAction } from "src/components/Form/Form.types.ts";
import { getModuleFirstTabPath } from "src/framework/openModule.ts";
import { useUser } from "src/auth/auth.ts";
import { useErpTranslation } from "src/system/useErpTranslation.ts";

export type Tab = {
  title: ReactNode | { (t: TFunction): ReactNode };
  disabledTooltip?:
    | ReactNode
    | { (params: { t: TFunction; data: any; values: any }): ReactNode };
  path: string;
  isDisabled?:
    | boolean
    | {
        (params: {
          data: any;
          clientConfig: ClientConfig;
          values: any;
        }): boolean;
      };
  isHidden?:
    | boolean
    | {
        (params: {
          data: any;
          clientConfig: ClientConfig;
          values: any;
        }): boolean;
      };
  key: string;
};

type TabsProps = {
  type: FormAction;
  resource: string;
  tabs: Tab[];
  idParam?: string;
  layoutForm?: boolean;
};

export const TabsFormLayoutV2 = (props: TabsProps) => {
  const { layoutForm } = props;
  if (layoutForm) {
    return <LayoutForm {...props} />;
  }
  return <RegularForm {...props} />;
};

const LayoutForm = (props: TabsProps) => {
  const { router } = useDataRouterContext();
  const clientConfig = useClientConfig();
  const { tabs: tabsProp, resource, type, idParam = "id" } = props;

  const params = useParams();
  const { data: user } = useUser();

  const modulePaths = useModuleRoutePaths({
    resource,
  });

  const { closeCreateTab, closeEditTab } = useModuleTabs();

  const handleSaveSuccess = (data: ValidSaveFormResponseData) => {
    if (type === "create") {
      // unfortunately there is such inconsistency in backend responses
      // sometimes backend returns just the id instead of the entire resource
      const id = data?.data?.id ?? data.id;
      router
        .navigate(
          replacePathParams(modulePaths.edit.root, {
            id: id,
          }),
        )
        .then(() => {
          closeCreateTab();
        });
    }
  };

  const handleDeleteSuccess = () => {
    router
      .navigate(
        getModuleFirstTabPath({
          resource,
          user: user!,
        }),
      )
      .then(() => {
        closeEditTab(params);
      });
  };

  const id = params[idParam];

  const { formContext, formApi, form, queryResult } = useResourceForm({
    action: type,
    resource,
    //@ts-ignore
    id,
    onSaveSuccess: handleSaveSuccess,
    onDeleteSuccess: handleDeleteSuccess,
  });
  const { data, isLoading, error, refetch, isFetching } = queryResult;

  const [tabsStore] = useState(new TabsStore());

  const storageKeys = useResourceStorageKeys({
    resource,
  });

  const { t } = useErpTranslation({
    resource,
    type: "layout",
  });

  const [paths, setPaths] = useState<RouterTabPath[]>(() =>
    (tabsProp || []).map((tab) => replacePathParams(tab.path, params)),
  );

  const values = useWatch({
    control: form.control,
  });

  const {
    tabs,
    activeTabId: activeTabKey,
    onActiveTabIdChange: setActiveTabKey,
  } = useRouterTabsV2<TabOptions>({
    paths: paths,
    config: useMemo(
      () =>
        (tabsProp || []).map((tab, index) => {
          return {
            shouldOpen: whenRoutePathIs(tab.path),
            mapToUiModel: (key) => {
              const isDisabled = getIsDisabled({
                data,
                isDisabled: tab.isDisabled,
                values,
                isLoading,
                type,
                clientConfig,
              });

              const isHidden = getIsHidden({
                data,
                isHidden: tab.isHidden,
                values,
                isLoading,
                type,
                clientConfig,
              });

              const content = (
                <Fragment key={tab.key}>
                  {isDisabled || isHidden || isLoading ? "" : <Outlet />}
                </Fragment>
              );
              const title =
                typeof tab.title === "function" ? tab.title(t) : tab.title;

              const storageKey = storageKeys.form({
                id: id!,
                type,
                tab: tab.key,
              });

              const hasUnsavedChanges = memoryStorageDriver.get(storageKey);

              const disabledTooltip =
                typeof tab.disabledTooltip === "function"
                  ? tab.disabledTooltip({
                      t,
                      data: data?.data,
                      values,
                    })
                  : tab.disabledTooltip;

              return {
                id: key,
                title: title,
                disabledTooltip,
                content: content,
                isDisabled,
                isHidden,
                hasUnsavedChanges,
              } as TabOptions;
            },
            insertAt: theEnd,
          };
        }),

      [
        t,
        tabsProp,
        storageKeys,
        type,
        id,
        isLoading,
        data,
        clientConfig,
        values,
      ],
    ),
    router,
    onPathsChange: setPaths,
  });

  useSubscribeTabsToFormUnsavedChanges({
    tabs: tabsProp,
    id,
    router,
    resource,
    type,
    tabsStore,
    paths,
  });

  return (
    <ResourceFormContext.Provider value={formContext}>
      <ResourceFormApiContext.Provider value={formApi}>
        <ReactHookFormFormProvider {...form}>
          <div css={loadingOverlayContainer}>
            <LoadingOverlay.Root
              isLoading={isLoading || (!!error && isFetching)}
            >
              <LoadingOverlay.Spinner />
              <LoadingOverlay.Title>
                {t("advices.pleaseWait", {
                  ns: TranslationNamespaceEnum.Common,
                })}
              </LoadingOverlay.Title>
            </LoadingOverlay.Root>
            {error ? (
              <QueryError
                error={error}
                onReset={() => {
                  refetch();
                }}
                resource={resource}
              />
            ) : (
              <SimpleTabsV2
                style={{
                  paddingTop: 15,
                }}
                hasControlledActiveTabId
                activeTabId={activeTabKey}
                onActiveTabIdChange={setActiveTabKey}
                tabs={tabs}
                store={tabsStore}
              />
            )}
          </div>
        </ReactHookFormFormProvider>
      </ResourceFormApiContext.Provider>
    </ResourceFormContext.Provider>
  );
};

const RegularForm = (props: TabsProps) => {
  const { router } = useDataRouterContext();
  const clientConfig = useClientConfig();

  const {
    tabs: tabsProp,
    resource,
    type,
    // todo: delete idParam prop
    idParam = "id",
  } = props;

  const params = useParams();

  const locale = useContentLocale() || "de";
  const id = params[idParam];
  const { data, isLoading, error, refetch, isFetching } = useOne({
    resource,
    id: id!,
    lang: locale,
    queryOptions: {
      enabled: type === "edit",
    },
  });

  const [tabsStore] = useState(new TabsStore());

  const storageKeys = useResourceStorageKeys({
    resource,
  });

  const { t } = useErpTranslation({
    resource,
    type: "layout",
  });

  const [paths, setPaths] = useState<RouterTabPath[]>(() =>
    (tabsProp || []).map((tab) => replacePathParams(tab.path, params)),
  );

  const {
    tabs,
    activeTabId: activeTabKey,
    onActiveTabIdChange: setActiveTabKey,
  } = useRouterTabsV2<TabOptions>({
    paths: paths,
    config: useMemo(
      () =>
        (tabsProp || []).map((tab, index) => {
          return {
            shouldOpen: whenRoutePathIs(tab.path),
            mapToUiModel: (key) => {
              const isDisabled = getIsDisabled({
                data,
                isDisabled: tab.isDisabled,
                values: undefined,
                isLoading,
                type,
                clientConfig,
              });

              const isHidden = getIsHidden({
                data,
                isHidden: tab.isHidden,
                values: undefined,
                isLoading,
                type,
                clientConfig,
              });

              const content = (
                <Fragment key={tab.key}>
                  {isDisabled || isHidden || isLoading ? "" : <Outlet />}
                </Fragment>
              );
              const title =
                typeof tab.title === "function" ? tab.title(t) : tab.title;

              const storageKey = storageKeys.form({
                id: id!,
                type,
                tab: tab.key,
              });

              const hasUnsavedChanges = !!memoryStorageDriver.get(storageKey);

              const disabledTooltip =
                typeof tab.disabledTooltip === "function"
                  ? tab.disabledTooltip({
                      t,
                      data: data?.data,
                      values: undefined,
                    })
                  : tab.disabledTooltip;

              return {
                id: key,
                title: title,
                disabledTooltip,
                content: content,
                isDisabled,
                isHidden,
                hasUnsavedChanges,
              } as TabOptions;
            },
            insertAt: theEnd,
          };
        }),
      [t, tabsProp, storageKeys, type, id, isLoading, data, clientConfig],
    ),
    router,
    onPathsChange: setPaths,
  });

  useSubscribeTabsToFormUnsavedChanges({
    tabs: tabsProp,
    id,
    resource,
    type,
    router,
    paths,
    tabsStore,
  });

  return (
    <div css={loadingOverlayContainer}>
      <LoadingOverlay.Root isLoading={isLoading || (!!error && isFetching)}>
        <LoadingOverlay.Spinner />
        <LoadingOverlay.Title>
          {t("advices.pleaseWait", {
            ns: TranslationNamespaceEnum.Common,
          })}
        </LoadingOverlay.Title>
      </LoadingOverlay.Root>
      {error ? (
        <QueryError
          error={error}
          onReset={() => {
            refetch();
          }}
          resource={resource}
        />
      ) : isLoading ? (
        <FakeTabs />
      ) : (
        <SimpleTabsV2
          style={{
            paddingTop: 15,
          }}
          hasControlledActiveTabId
          activeTabId={activeTabKey}
          onActiveTabIdChange={setActiveTabKey}
          tabs={tabs}
          store={tabsStore}
        />
      )}
    </div>
  );
};

const FakeTabs = () => {
  const { t } = useTranslation();
  const [tabsStore] = useState(() => new TabsStore());
  const [tabs] = useState([
    {
      title: t("formTabs.general", {
        ns: TranslationNamespaceEnum.Common,
      }),
      id: "general",
    },
  ]);
  return (
    <SimpleTabsV2
      style={{
        paddingTop: 15,
      }}
      hasControlledActiveTabId
      activeTabId={"general"}
      onActiveTabIdChange={() => {}}
      tabs={tabs}
      store={tabsStore}
    />
  );
};

const getIsHidden = ({
  isHidden,
  data,
  type,
  clientConfig,
  isLoading,
  values,
}: {
  values: any;
  isHidden?:
    | boolean
    | {
        (params: {
          data: ValidFormData | undefined;
          clientConfig: ClientConfig;
          values: any;
        }): boolean;
      };
  data: ValidFormData | undefined;
  type: "edit" | "create";
  clientConfig: ClientConfig;
  isLoading: boolean;
}) => {
  if (typeof isHidden === "function" && type === "create") {
    return isHidden({
      data: undefined,
      clientConfig,
      values,
    });
  } else if (typeof isHidden === "function" && type === "edit") {
    if (data?.data) {
      return isHidden({
        data: data?.data,
        clientConfig,
        values,
      });
    }
    return isLoading;
  }

  return isHidden;
};

const getIsDisabled = ({
  isDisabled,
  data,
  type,
  clientConfig,
  isLoading,
  values,
}: {
  values: any;
  isDisabled?:
    | boolean
    | {
        (params: {
          data: ValidFormData | undefined;
          clientConfig: ClientConfig;
          values: any;
        }): boolean;
      };
  data: ValidFormData | undefined;
  type: "edit" | "create";
  clientConfig: ClientConfig;
  isLoading: boolean;
}) => {
  if (typeof isDisabled === "function" && type === "edit") {
    if (data?.data) {
      return isDisabled({
        data: data?.data,
        clientConfig,
        values,
      });
    }
    return isLoading;
  }
  if (typeof isDisabled === "function" && type === "create") {
    return isDisabled({
      data: undefined,
      clientConfig,
      values,
    });
  }

  return isDisabled;
};

const loadingOverlayContainer = css({
  position: "relative",
  width: "100%",
  height: "100%",
});
